import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { AfterViewInit, Component, Inject, QueryList, ViewChildren } from '@angular/core';
import { ReactiveFormsModule, UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Application, ApplicationQuestion, ButtonConfig, Client, Job } from '@interfaces';
import { Observable } from 'rxjs';
import { GtagService } from 'src/app/containers/gtag/gtag.service';
import { calculateButtonClasses } from 'src/app/helpers/buttons';
import { ApplicationsService } from 'src/app/rest/applications.service';
import { FeedbackService } from 'src/app/rest/feedback.service';
import { SubdomainService } from 'src/app/rest/subdomain.service';
import { slugify } from '../../../helpers/string-utils';
import { DialogContainerComponent } from '../../dialog-container/dialog-container.component';
import { StarRatingComponent } from '../../star-rating/star-rating/star-rating.component';

@Component({
	selector: 'app-apply-form',
	templateUrl: './apply-form.component.html',
	styleUrls: ['./apply-form.component.css'],
	standalone: true,
	imports: [CommonModule, ReactiveFormsModule, DialogContainerComponent, StarRatingComponent],
})
export class ApplyFormComponent implements AfterViewInit {
	@ViewChildren(StarRatingComponent) starRating: QueryList<StarRatingComponent>;
	public applyForm: UntypedFormGroup;
	public ratingForm: UntypedFormGroup;
	public job: Job;
	public skillLevels = ['', 'None','Basic','Average','Advanced','Expert'];
	public sanitisedSkills = [];
	public submitted = false;
	public submitting = false;
	public finalised = false;
	public isMobile = true;
    public error = false;
	public client$: Observable<Client>;
	public applyButtonConfig: ButtonConfig = {
        textColour: "text-white",
        background: {
            type: "custom",
            value: "bg-blue-500",
        },
        textSize: "regular",
    };
	public applyButtonClasses = calculateButtonClasses(this.applyButtonConfig);
	get customQuestionsArr(): UntypedFormArray {
		return (this.applyForm.get('customQuestions') as UntypedFormArray);
	}

	constructor(
		@Inject(DIALOG_DATA) public data: any,
		public dialogRef: DialogRef<boolean>,
		private fb: UntypedFormBuilder,
		private applyService: ApplicationsService,
		private feedback: FeedbackService,
		private route: ActivatedRoute,
		private gtag: GtagService,
		private subdomain: SubdomainService,
	) {
		this.job = data.job;
		this.client$ = this.subdomain.getClient();
		const formConfig = {
			firstName: ['', Validators.required],
			lastName: ['', Validators.required],
			email: ['', [Validators.required, Validators.email]],
			phone: [''],
			location: [],
            profileUrl: [],
			employer: [this.job.employer],
			referral: [this.route.snapshot.queryParams.referral],
			skills: new UntypedFormGroup({}),
			customQuestions: new UntypedFormArray([]),
			relatedJobsConsent: [false],
			terms: [false, [Validators.requiredTrue]],
            cv: [null, this.job.delayCvOnMobile ? [] : [Validators.required]],
            source: [this.route.snapshot.queryParams?.agencyRef ?? null]
		};

		if (this.job.rightToWorkInUK) {
			formConfig['rightToWorkInUK'] = ['', Validators.required];
		}

		if (this.job.skills) {
			this.job.skills.forEach(item => {
				item = slugify(item);
				this.sanitisedSkills.push(item);
				formConfig['skills'].addControl(item, new UntypedFormControl(1));
			});
		}

		if (this.job.customQuestions) {
			this.job.customQuestions.forEach((item: ApplicationQuestion) => {
				const newControl = this.fb.group({
					question: [item.text],
					type: [item.type],
					answer: [''],
					required: [item.required],
				});

				if(item.required && item.type !== 'checkbox') {
					newControl.get('answer').setValidators([Validators.required]);
				} else if (item.required && item.type === 'checkbox') {
					newControl.get('answer').setValidators([Validators.requiredTrue]);
				}

				formConfig['customQuestions'].push(newControl);
			});
		}
		this.applyForm = this.fb.group(formConfig);

		const ratingFormConfig = {
			rating: [3, Validators.required],
			application: [],
			job: [],
			employer: [],
			email: [],
			name: []
		};
		this.ratingForm = this.fb.group(ratingFormConfig);
	}

	ngAfterViewInit() {
		this.starRating.changes.subscribe((result: QueryList<StarRatingComponent>) => {
			if (result?.first) {
				result.first.ratingUpdated.subscribe(this.handleRatingChange.bind(this));
			}
		});
	}

	public onFileUpload(event) {
		const me = this;
		const files = event.target.files || event.srcElement.files;
		const file = files[0];
		const reader: FileReader = new FileReader();

		reader.onloadend = e => {
			me.applyForm.get('cv').setValue({
				data: (reader.result as any).split(',')[1],
				filename: file.name,
				type: file.type
			});
		};
		reader.readAsDataURL(file);
	}

	public getSkillValue(skill) {
		const control = this.applyForm.get('skills').get(skill);
		if(control) {
			return this.skillLevels[control.value];
		} else {
			return '';
		}
	}

	public apply() {
        this.error = false;
		this.submitting = true;
		const payload = {
			...this.applyForm.value,
		};
		payload.job = this.job._id;

		if(!this.job.active) {
			payload.stage = 'register-interest';
		}

		this.applyService.apply(payload).subscribe((result: Application) => {
            this.gtag.fireApplyEvent(this.job._id, this.job.title, result._id);
			this.ratingForm.get('application').setValue(result._id);
			this.ratingForm.get('employer').setValue(result.employer);
			this.ratingForm.get('job').setValue(result.jobData._id);
			this.submitted = true;
			this.submitting = false;
		}, (error: HttpErrorResponse) => {
			this.submitting = false;
			if(error.status === 406) {
				this.applyForm.controls.referral.setErrors({
					'unrecognised': $localize`Your referral code was unrecognised`
				});
			} else {
				this.error = true;
			}
		});
	}

	private handleRatingChange(value: number) {
		this.ratingForm.get('rating').setValue(value);
		this.submitFeedback();
	}

	private submitFeedback() {
		this.feedback.post(this.ratingForm.getRawValue()).subscribe(() => {
			this.finalised = true;
		});
	}

    public closeDialog(status = false) {
		this.dialogRef.close(status);
	}

}
